import React, { useState, useEffect } from "react";
import Header from "../Component/Header";
import { Input, Form, Layout, Menu, Select, Button, List, Spin } from "antd";
import { PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { uploadData, remove } from "aws-amplify/storage";

import {
  useDisplayProjects,
  useUpdateProject,
  useInsertProject,
} from "../Hooks/homepage/project";
import { useDisplayClients } from "../Hooks/homepage/client";
import {
  useDisplayProjectOutcome,
  useInsertProjectOutcome,
  useDeleteProjectOutcome,
} from "../Hooks/homepage/outcome";
import AddableCardList from "../Component/AddableCardList";
import ModifyClientModal from "../Component/ModifyClientModal";
import { v4 as uuidv4 } from "uuid";

const HomePageEdit = () => {
  const [selectedProject, setSelectedProject] = useState("0");
  const [sideMenu, setSideMenu] = useState();
  const [editPicture, setEditPicture] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  const { data: projectList } = useDisplayProjects();
  const { data: clientList } = useDisplayClients();
  const { data: projectOutcomeList } = useDisplayProjectOutcome();
  const [insertProjectOutcome] = useInsertProjectOutcome();
  const [deleteProjectOutcome] = useDeleteProjectOutcome();
  const [insertProject] = useInsertProject();
  const [updateProject] = useUpdateProject();
  const [loading, setLoading] = useState(false);

  const [filesData, setFilesData] = useState({});

  const [inputUrl, setInputUrl] = useState({
    background: "",
    showcase: [],
    separate_bg: [],
    client_img: [],
  });

  const [detailsForm] = Form.useForm();

  useEffect(() => {
    const defaultSideMenu = ["Add new Project"].map((key, index) => ({
      key: index,
      label: `${key}`,
      icon: <PlusCircleOutlined />,
    }));
    const projectSideMenu = projectList?.home_page_project.map((project) => ({
      key: project.id,
      label: `${project.title}`,
    }));
    if (projectSideMenu) {
      const sideMenu = [...defaultSideMenu, ...projectSideMenu];
      setSideMenu(sideMenu);
    }
  }, [projectList]);

  useEffect(() => {
    if (selectedProject && selectedProject !== "0") {
      const selectedProjectData = projectList?.home_page_project.find(
        (project) => project.id === selectedProject
      );
      if (selectedProjectData) {
        const formData = {
          introduction: Array.isArray(selectedProjectData.introduction)
            ? selectedProjectData.introduction.join("\n")
            : selectedProjectData.introduction || "",
          client_img: selectedProjectData.client_img || [],
          title: selectedProjectData.title || "",
          link: selectedProjectData.link || "",
          location: selectedProjectData.location || "",
          sector: selectedProjectData.sector || "",
        };

        setFilesData((prev) => ({
          ...prev,
          id: selectedProjectData.id,
          image: selectedProjectData.image || null,
          showcase: selectedProjectData.showcase || [],
          separate_bg: selectedProjectData.separate_bg || [],
          introduction: formData.introduction,
          client_img: formData.client_img,
          title: formData.title,
          link: formData.link,
          location: formData.location,
          sector: formData.sector,
        }));

        detailsForm.setFieldsValue(formData);
      }
    } else {
      setEditPicture(false);
      setFilesData((prev) => ({
        ...prev,
        id: null,
        image: null,
        showcase: [],
        separate_bg: [],
        introduction: "",
        client_img: [],
        title: "",
        link: "",
        location: "",
        sector: "",
      }));
      detailsForm.resetFields();
    }
  }, [selectedProject, projectList, detailsForm]);

  useEffect(() => {
    if (projectOutcomeList) {
      const outcomeData = projectOutcomeList.home_page_project_outcome.reduce(
        (acc, outcome) => {
          acc[outcome.id] = {
            id: outcome.id,
            projectId: outcome.home_page_project_id,
            title: outcome.title,
            link: outcome.link,
            content: outcome.content,
            img: outcome.img,
            isLocal: false,
            isLoading: false,
          };
          return acc;
        },
        {}
      );
      setFilesData((prev) => ({
        ...prev,
        outcome: {
          ...prev.outcome,
          ...outcomeData,
        },
      }));
    }
  }, [projectOutcomeList]);

  const PathDisplay = ({ url, onDelete }) => (
    <List.Item style={{ padding: "8px 0" }}>
      <div style={{ wordBreak: "break-all" }}>
        {url}
        <Button type="text" danger onClick={onDelete}>
          Delete
        </Button>
      </div>
    </List.Item>
  );

  const handleFileUpload = async (file, fieldType) => {
    try {
      setLoading(true);

      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop();
      const fileName = `${timestamp}.${fileExtension}`;

      let projectName = "new_project";
      if (selectedProject !== "0") {
        const currentProject = projectList?.home_page_project.find(
          (project) => project.id === selectedProject
        );
        if (currentProject && currentProject.title) {
          projectName = currentProject.title;
        }
      }

      const s3Path = `public/websitePicture/${projectName}/${fileName}`;

      await uploadData({
        path: s3Path,
        data: file,
      }).result;

      const uploadedUrl = s3Path.replace("public/", "");

      if (selectedProject !== "0") {
        let updateObject = {};

        switch (fieldType) {
          case "image":
            updateObject = { image: uploadedUrl };
            break;
          case "showcase":
            const newShowcase = [...(filesData.showcase || []), uploadedUrl];
            updateObject = { showcase: newShowcase };
            break;
          case "separate_bg":
            const newSeparateBg = [
              ...(filesData.separate_bg || []),
              uploadedUrl,
            ];
            updateObject = { separate_bg: newSeparateBg };
            break;
          default:
            break;
        }

        await updateProject({
          variables: {
            id: filesData.id,
            object: updateObject,
          },
        });

        setFilesData((prev) => ({
          ...prev,
          ...updateObject,
        }));

        updateFormValue(fieldType, updateObject[fieldType]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setLoading(false);
    }
  };

  const handleOutcomeFileUpload = async (file, outcomeId) => {
    try {
      setLoading(true);

      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop();
      const fileName = `${timestamp}.${fileExtension}`;

      let projectName = "new_project";
      if (selectedProject !== "0") {
        const currentProject = projectList?.home_page_project.find(
          (project) => project.id === selectedProject
        );
        if (currentProject && currentProject.title) {
          projectName = currentProject.title;
        }
      }

      const s3Path = `public/websitePicture/${projectName}/${fileName}`;

      await uploadData({
        path: s3Path,
        data: file,
      }).result;

      const uploadedUrl = s3Path.replace("public/", "");

      setFilesData((prev) => ({
        ...prev,
        outcome: {
          ...prev.outcome,
          [outcomeId]: {
            ...prev.outcome[outcomeId],
            img: uploadedUrl,
          },
        },
      }));

      setLoading(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setLoading(false);
    }
  };

  const FileUploadButton = ({ fieldType, children }) => {
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileUpload(file, fieldType);
      }
    };

    return (
      <>
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          id={`file-upload-${fieldType}`}
          onChange={handleFileChange}
        />
        <Button
          icon={<UploadOutlined />}
          onClick={() =>
            document.getElementById(`file-upload-${fieldType}`).click()
          }
        >
          {children}
        </Button>
      </>
    );
  };

  const OutcomeFileUploadButton = ({ outcomeId, children }) => {
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleOutcomeFileUpload(file, outcomeId);
      }
    };

    return (
      <>
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          id={`file-upload-outcome-${outcomeId}`}
          onChange={handleFileChange}
        />
        <Button
          icon={<UploadOutlined />}
          onClick={() =>
            document.getElementById(`file-upload-outcome-${outcomeId}`).click()
          }
        >
          {children}
        </Button>
      </>
    );
  };

  useEffect(() => {
    if (projectOutcomeList) {
      const outcomeData = projectOutcomeList.home_page_project_outcome.reduce(
        (acc, outcome) => {
          acc[outcome.id] = {
            id: outcome.id,
            projectId: outcome.home_page_project_id,
            title: outcome.title,
            link: outcome.link,
            content: outcome.content,
            img: outcome.img,
            isLocal: false,
            isLoading: false,
          };
          return acc;
        },
        {}
      );
      setFilesData((prev) => ({
        ...prev,
        outcome: {
          ...prev.outcome,
          ...outcomeData,
        },
      }));
    }
  }, [projectOutcomeList]);

  const updateFormValue = (field, value) => {
    setFilesData((prev) => ({
      ...prev,
      [field]: value,
    }));
    detailsForm.setFieldsValue({
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await detailsForm.validateFields();

      // Separate introduction into paragraphs
      let introductionArray = [];
      if (values.introduction) {
        // Split by newline and filter out empty paragraphs
        introductionArray = values.introduction
          .split("\n")
          .map((paragraph) => paragraph.trim())
          .filter((paragraph) => paragraph !== "");

        // If there are no paragraphs, set to empty array
        if (introductionArray.length === 0) {
          introductionArray = [];
        }
      }

      // Create the data object to submit
      const submitData = {
        ...values,
        introduction: introductionArray,
      };

      console.log("Submit Data:", submitData);

      if (selectedProject === "0") {
        setLoading(true);
        insertProject({
          variables: {
            object: {
              ...submitData,
              image: null,
              showcase: [],
              separate_bg: [],
            },
          },
        })
          .then(() => {
            setLoading(false);
            setSelectedProject("0");
            console.log("Project inserted successfully");
          })
          .catch((error) => {
            setLoading(false);
            console.error("Error inserting project:", error);
          });
      } else {
        setLoading(true);
        updateProject({
          variables: {
            id: filesData.id,
            object: {
              ...submitData,
            },
          },
        })
          .then(() => {
            setLoading(false);
            console.log("Project updated successfully");
          })
          .catch((error) => {
            setLoading(false);
            console.error("Error updating project:", error);
          });
      }
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const renderOutcomeItem = (outcome, index) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <Input
        placeholder="Title"
        value={outcome.title}
        onChange={(e) => {
          setFilesData((prev) => ({
            ...prev,
            outcome: {
              ...prev.outcome,
              [outcome.id]: {
                ...prev.outcome[outcome.id],
                title: e.target.value,
              },
            },
          }));
        }}
      />

      <Input
        placeholder="Link"
        value={outcome.link}
        onChange={(e) => {
          setFilesData((prev) => ({
            ...prev,
            outcome: {
              ...prev.outcome,
              [outcome.id]: {
                ...prev.outcome[outcome.id],
                link: e.target.value,
              },
            },
          }));
        }}
      />

      <Input.TextArea
        placeholder="Content"
        rows={4}
        value={outcome.content}
        onChange={(e) => {
          setFilesData((prev) => ({
            ...prev,
            outcome: {
              ...prev.outcome,
              [outcome.id]: {
                ...prev.outcome[outcome.id],
                content: e.target.value,
              },
            },
          }));
        }}
      />

      {outcome.img && outcome.img.includes("http") ? (
        <PathDisplay
          url={outcome.img}
          onDelete={() => {
            setFilesData((prev) => ({
              ...prev,
              outcome: {
                ...prev.outcome,
                [outcome.id]: {
                  ...prev.outcome[outcome.id],
                  img: "",
                },
              },
            }));
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Input
            placeholder="Image URL"
            value={outcome.img}
            onChange={(e) => {
              setFilesData((prev) => ({
                ...prev,
                outcome: {
                  ...prev.outcome,
                  [outcome.id]: {
                    ...prev.outcome[outcome.id],
                    img: e.target.value,
                  },
                },
              }));
            }}
          />
          <div style={{ marginTop: 16 }}>
            <OutcomeFileUploadButton outcomeId={outcome.id}>
              Click to Upload Cover Image
            </OutcomeFileUploadButton>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ height: "100vh" }}>
      <Spin spinning={loading}>
        <Header select="Home Page Edit">
          <Layout>
            <Layout.Sider
              width={200}
              style={{
                background: "#fff",
                overflowY: "auto",
                position: "fixed",
                height: "calc(100vh - 64px)",
                top: 64,
              }}
            >
              <Menu
                mode="inline"
                selectedKeys={[selectedProject]}
                items={sideMenu}
                onClick={({ key }) => {
                  setEditPicture(false);
                  if (key === "0") {
                    setSelectedProject("0");

                    detailsForm.resetFields();
                  } else {
                    setSelectedProject(key);
                  }
                }}
                style={{ height: "100%" }}
              />
            </Layout.Sider>
            <Layout.Content
              style={{
                padding: "16px",
                backgroundColor: "#fff",
                overflowY: "auto",
                height: "100vh",
              }}
            >
              <Form
                form={detailsForm}
                layout="vertical"
                style={{
                  marginLeft: 220,
                  padding: "16px",
                  display: editPicture ? "none" : "block",
                }}
              >
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[
                    { required: true, message: "Please input the title!" },
                  ]}
                >
                  <Input
                    placeholder="Title"
                    onChange={(e) => {
                      const titleValue = e.target.value;
                      updateFormValue("title", titleValue);
                      // Generate link from title
                      const linkValue =
                        "/home_project/" +
                        titleValue.trim().toLowerCase().replace(/\s+/g, "_");
                      updateFormValue("link", linkValue);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Link"
                  name="link"
                  rules={[
                    { required: true, message: "Please input the link!" },
                  ]}
                >
                  <Input placeholder="Link" />
                </Form.Item>
                <Form.Item
                  label="Client"
                  name="client_img"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one client!",
                    },
                  ]}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Select
                      mode="multiple"
                      placeholder="Select a client"
                      options={clientList?.home_page_project_client.map(
                        (client) => ({
                          label: client.name,
                          value: client.path,
                        })
                      )}
                      value={detailsForm.getFieldValue("client_img") || []}
                      onChange={(value) => {
                        updateFormValue("client_img", value);

                        setInputUrl((prev) => ({
                          ...prev,
                          client_img: value,
                        }));
                      }}
                      allowClear
                    />
                    <Button
                      onClick={() => {
                        setShowNewClientModal(true);
                      }}
                      style={{ marginLeft: "8px" }}
                    >
                      {" "}
                      Edit Client List
                    </Button>
                  </div>
                </Form.Item>
                <Form.Item
                  label="Location"
                  name="location"
                  rules={[
                    { required: true, message: "Please input the location!" },
                  ]}
                >
                  <Input placeholder="Location" />
                </Form.Item>
                <Form.Item
                  label="Sector"
                  name="sector"
                  rules={[
                    { required: true, message: "Please input the sector!" },
                  ]}
                >
                  <Input placeholder="Sector" />
                </Form.Item>
                <Form.Item label="Introduction" name="introduction">
                  <Input.TextArea placeholder="Introduction" rows={4} />
                </Form.Item>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    style={{
                      display: selectedProject === "0" ? "none" : "block",
                    }}
                    onClick={() => setEditPicture(true)}
                  >
                    Edit the photos and files
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    style={{ marginLeft: "16px" }}
                  >
                    Submit
                  </Button>
                </div>
              </Form>
              <Form
                layout="vertical"
                style={{
                  marginLeft: 220,
                  padding: "16px",
                  display: editPicture ? "block" : "none",
                }}
              >
                <Form.Item label="Background Image" name="image">
                  {filesData.image ? (
                    <PathDisplay
                      url={filesData.image}
                      onDelete={() => {
                        setLoading(true);

                        if (!filesData.image.includes("http")) {
                          try {
                            remove({ path: "public/" + filesData.image });
                          } catch (error) {
                            console.error(
                              "Error deleting file from S3:",
                              error
                            );
                          }
                        }
                        updateProject({
                          variables: {
                            id: filesData.id,
                            object: {
                              image: null,
                            },
                          },
                        }).then(() => {
                          setLoading(false);
                          updateFormValue("image", null);
                          setFilesData((prev) => ({
                            ...prev,
                            image: null,
                          }));
                        });
                      }}
                    />
                  ) : (
                    <>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Input
                          placeholder="Background Image URL (Image only)"
                          onChange={(e) => {
                            setInputUrl((prev) => ({
                              ...prev,
                              background: e.target.value,
                            }));
                          }}
                        />
                        <Button
                          onClick={() => {
                            if (inputUrl.background.length === 0) {
                            } else {
                              setLoading(true);
                              updateProject({
                                variables: {
                                  id: filesData.id,
                                  object: {
                                    image: inputUrl.background,
                                  },
                                },
                              }).then(() => {
                                setLoading(false);
                                updateFormValue("image", inputUrl.background);
                                setFilesData((prev) => ({
                                  ...prev,
                                  image: inputUrl.background,
                                }));
                                setInputUrl((prev) => ({
                                  ...prev,
                                  background: [],
                                }));
                              });
                            }
                          }}
                          style={{ marginLeft: "8px" }}
                        >
                          {" "}
                          Add to BG Image
                        </Button>
                      </div>
                      <div
                        style={{
                          paddingLeft: 64,
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        Or
                      </div>
                      <FileUploadButton fieldType="image">
                        Click to Upload
                      </FileUploadButton>
                    </>
                  )}
                </Form.Item>
                <Form.Item label="Showcase" name="showcase">
                  {filesData.showcase?.map((item, index) => (
                    <PathDisplay
                      key={index}
                      url={item}
                      onDelete={() => {
                        setLoading(true);
                        if (!filesData.showcase[index].includes("http")) {
                          try {
                            remove({
                              path: "public/" + filesData.showcase[index],
                            });
                          } catch (error) {
                            console.error(
                              "Error deleting file from S3:",
                              error
                            );
                          }
                        }
                        updateProject({
                          variables: {
                            id: filesData.id,
                            object: {
                              showcase: filesData.showcase.filter(
                                (_, i) => i !== index
                              ),
                            },
                          },
                        }).then(() => {
                          setLoading(false);
                        });
                      }}
                    />
                  ))}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      placeholder="Showcase URL (Either Image or Video)"
                      onChange={(e) => {
                        setInputUrl((prev) => ({
                          ...prev,
                          showcase: e.target.value,
                        }));
                      }}
                    />
                    <Button
                      onClick={() => {
                        if (inputUrl.showcase.length === 0) {
                        } else {
                          const newShowcase = [...filesData.showcase];
                          newShowcase.push(inputUrl.showcase);
                          setLoading(true);
                          updateProject({
                            variables: {
                              id: filesData.id,
                              object: {
                                showcase: newShowcase,
                              },
                            },
                          }).then(() => {
                            setLoading(false);
                            updateFormValue("showcase", newShowcase);
                            setFilesData((prev) => ({
                              ...prev,
                              showcase: newShowcase,
                            }));
                            setInputUrl((prev) => ({
                              ...prev,
                              showcase: [],
                            }));
                          });
                        }
                      }}
                      style={{ marginLeft: "8px" }}
                    >
                      {" "}
                      Add to Showcase
                    </Button>
                  </div>
                  <div
                    style={{
                      paddingLeft: 64,
                      paddingTop: 12,
                      paddingBottom: 12,
                    }}
                  >
                    Or
                  </div>
                  <FileUploadButton fieldType="showcase">
                    Click to Upload
                  </FileUploadButton>
                </Form.Item>
                <Form.Item label="Outcome" name="outcome">
                  <AddableCardList
                    items={
                      filesData.outcome
                        ? Object.values(filesData.outcome).filter(
                            (outcome) => outcome.projectId === selectedProject
                          )
                        : []
                    }
                    onAdd={() => {
                      const id = uuidv4();
                      const newOutcome = {
                        id,
                        projectId: selectedProject,
                        title: "",
                        link: "",
                        content: "",
                        img: "",
                        isLocal: true,
                        isLoading: false,
                      };
                      setFilesData((prev) => ({
                        ...prev,
                        outcome: {
                          ...prev.outcome,
                          [id]: newOutcome,
                        },
                      }));
                      console.log(filesData);
                    }}
                    onAddConfirm={(item) => {
                      setFilesData((prev) => ({
                        ...prev,
                        outcome: {
                          ...prev.outcome,
                          [item.id]: {
                            ...item,
                            isLoading: true,
                          },
                        },
                      }));
                      insertProjectOutcome({
                        variables: {
                          object: {
                            id: item.id,
                            img: item.img,
                            title: item.title,
                            link: item.link,
                            content: item.content,
                            home_page_project_id: selectedProject,
                          },
                        },
                      })
                        .then(() => {
                          console.log("Insert Success!");
                          setFilesData((prev) => ({
                            ...prev,
                            outcome: {
                              ...prev.outcome,
                              [item.id]: {
                                ...item,
                                isLoading: false,
                                isLocal: false,
                              },
                            },
                          }));
                        })
                        .catch((error) => {
                          console.error("Error inserting outcome:", error);
                        });

                      setTimeout(() => {}, 1000);
                    }}
                    onCancel={(outcome) => {
                      setFilesData((prev) => {
                        const newOutcome = { ...prev.outcome };
                        delete newOutcome[outcome.id];
                        return {
                          ...prev,
                          outcome: newOutcome,
                        };
                      });
                      if (
                        outcome.isLocal &&
                        outcome.img &&
                        !outcome.img.includes("http")
                      ) {
                        try {
                          remove({ path: "public/" + outcome.img });
                        } catch (error) {
                          console.error("Error deleting file from S3:", error);
                        }
                      }
                    }}
                    onRemove={(outcome) => {
                      setFilesData((prev) => ({
                        ...prev,
                        outcome: {
                          ...prev.outcome,
                          [outcome.id]: {
                            ...outcome,
                            isLoading: true,
                          },
                        },
                      }));
                      if (outcome.img && !outcome.img.includes("http")) {
                        try {
                          remove({ path: "public/" + outcome.img });
                        } catch (error) {
                          console.error("Error deleting file from S3:", error);
                        }
                      }

                      deleteProjectOutcome({
                        variables: { id: outcome.id },
                      })
                        .then(() => {
                          setFilesData((prev) => {
                            const {
                              [outcome.id]: removed,
                              ...remainingOutcomes
                            } = prev.outcome;

                            return {
                              ...prev,
                              outcome: remainingOutcomes,
                            };
                          });
                        })
                        .catch((error) => {
                          console.error("Error deleting outcome:", error);
                        });
                    }}
                    itemName="Outcome"
                    renderItem={renderOutcomeItem}
                    addButtonText="Add New Outcome"
                    colSpan={12}
                  />
                </Form.Item>
                <Form.Item label="Separate Picture" name="separate_bg">
                  {filesData.separate_bg?.map((item, index) => (
                    <PathDisplay
                      key={index}
                      url={item}
                      onDelete={() => {
                        setLoading(true);
                        if (!filesData.separate_bg[index].includes("http")) {
                          try {
                            remove({
                              path: "public/" + filesData.separate_bg[index],
                            });
                          } catch (error) {
                            console.error(
                              "Error deleting file from S3:",
                              error
                            );
                          }
                        }
                        updateProject({
                          variables: {
                            id: filesData.id,
                            object: {
                              separate_bg: filesData.separate_bg.filter(
                                (_, i) => i !== index
                              ),
                            },
                          },
                        }).then(() => {
                          setLoading(false);
                        });
                      }}
                    />
                  ))}
                  <div
                    style={{
                      display:
                        filesData.separate_bg &&
                        filesData.separate_bg.length >= 2
                          ? "none"
                          : "",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Input
                        placeholder="Separate Picture URL"
                        onChange={(e) => {
                          setInputUrl((prev) => ({
                            ...prev,
                            separate_bg: e.target.value,
                          }));
                        }}
                      />
                      <Button
                        onClick={() => {
                          if (inputUrl.separate_bg.length === 0) {
                          } else {
                            const newSeparateBg = [...filesData.separate_bg];
                            newSeparateBg.push(inputUrl.separate_bg);
                            setLoading(true);
                            updateProject({
                              variables: {
                                id: filesData.id,
                                object: {
                                  separate_bg: newSeparateBg,
                                },
                              },
                            }).then(() => {
                              setLoading(false);
                              updateFormValue("separate_bg", newSeparateBg);
                              setFilesData((prev) => ({
                                ...prev,
                                separate_bg: newSeparateBg,
                              }));
                              setInputUrl((prev) => ({
                                ...prev,
                                separate_bg: [],
                              }));
                            });
                          }
                        }}
                        style={{ marginLeft: "8px" }}
                      >
                        {" "}
                        Add to Separate Picture
                      </Button>
                    </div>
                    <div
                      style={{
                        paddingLeft: 64,
                        paddingTop: 12,
                        paddingBottom: 12,
                      }}
                    >
                      Or
                    </div>
                    <FileUploadButton fieldType="separate_bg">
                      Click to Upload
                    </FileUploadButton>
                  </div>
                </Form.Item>
                <Form.Item style={{ textAlign: "center" }}>
                  <Button onClick={() => setEditPicture(false)}>
                    Edit details
                  </Button>
                </Form.Item>
              </Form>
              <ModifyClientModal
                open={showNewClientModal}
                onCancel={() => setShowNewClientModal(false)}
              />
            </Layout.Content>
          </Layout>
        </Header>
      </Spin>
    </Layout>
  );
};

export default HomePageEdit;
